import { useTranslation } from 'react-i18next';
import {
  iconArrowRightHeaderCourses,
  iconSaveHeaderCourses,
} from '../../assets';
import { useTheme } from '../../theme';

export interface CreateCourseHeaderProps {
  /** Conteúdo do link "voltar" (ex.: <Link to="/admin/courses">← Back to Courses</Link>) */
  backLink: React.ReactNode;
  onSaveDraft?: () => void;
  /** Desabilita o botão Save Draft durante o salvamento (ex.: loading) */
  saveDraftDisabled?: boolean;
  onPublish?: () => void;
  /** Desabilita o botão Publish durante o salvamento/publicação (ex.: loading) */
  publishDisabled?: boolean;
  onNextStep?: () => void;
  /** Desabilita o botão Next step (ex.: no step 3) */
  nextStepDisabled?: boolean;
  /** Identificador de teste para o header do Create Course */
  dataE2eHeader?: string;
  /** Identificador de teste para o botão Save Draft */
  dataE2eSaveDraftButton?: string;
  /** Identificador de teste para o botão Publish */
  dataE2ePublishButton?: string;
  /** Identificador de teste para o botão Next Step */
  dataE2eNextStepButton?: string;
}

export function CreateCourseHeader({
  backLink,
  onSaveDraft,
  saveDraftDisabled = false,
  onPublish,
  publishDisabled = false,
  onNextStep,
  nextStepDisabled = false,
  dataE2eHeader,
  dataE2eSaveDraftButton,
  dataE2ePublishButton,
  dataE2eNextStepButton,
}: CreateCourseHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const linkWrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.text.black,
  };

  const separatorStyle: React.CSSProperties = {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: theme.palette.border.main,
    margin: `0 ${theme.spacing.md}`,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xl,
    color: theme.palette.text.primary,
    margin: 0,
    lineHeight: theme.typography.lineHeight.tight,
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.normal,
    color: theme.palette.text.secondary,
    margin: 0,
    marginTop: theme.spacing.xs,
  };

  const headerBarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${theme.spacing.md} 0`,
    paddingLeft: theme.spacing.lg,
    paddingRight: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    backgroundColor: theme.palette.background.paper,
  };

  const titleBlockStyle: React.CSSProperties = {
    flex: 1,
    marginLeft: theme.spacing.sm,
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexShrink: 0,
  };

  const baseButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    borderRadius: theme.shape.borderRadius.sm,
    border: 'none',
    cursor: 'pointer',
  };

  return (
    <header style={headerBarStyle} data-e2e={dataE2eHeader}>
      <div style={linkWrapperStyle}>{backLink}</div>
      <div style={separatorStyle} aria-hidden />
      <div style={titleBlockStyle}>
        <h1 style={titleStyle}>{t('courses.create.title')}</h1>
        <p style={subtitleStyle}>{t('courses.create.subtitle')}</p>
      </div>
      <div style={actionsStyle}>
        {onSaveDraft != null && (
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={saveDraftDisabled}
            style={{
              ...baseButtonStyle,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              cursor: saveDraftDisabled ? 'not-allowed' : 'pointer',
              opacity: saveDraftDisabled ? 0.7 : 1,
            }}
            data-e2e={dataE2eSaveDraftButton}
          >
            <img
              src={iconSaveHeaderCourses}
              alt=""
              width={18}
              height={18}
              aria-hidden
              style={{ display: 'block' }}
            />
            {saveDraftDisabled
              ? t('courses.create.saving_draft')
              : t('courses.create.save_draft')}
          </button>
        )}
        {onPublish != null && (
          <button
            type="button"
            onClick={onPublish}
            disabled={publishDisabled}
            style={{
              ...baseButtonStyle,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              cursor: publishDisabled ? 'not-allowed' : 'pointer',
              opacity: publishDisabled ? 0.7 : 1,
            }}
            data-e2e={dataE2ePublishButton}
          >
            {publishDisabled
              ? t('courses.create.publishing')
              : t('courses.create.publish')}
          </button>
        )}
        <button
          type="button"
          onClick={onNextStep}
          disabled={nextStepDisabled}
          data-e2e={dataE2eNextStepButton}
          style={{
            ...baseButtonStyle,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            cursor: nextStepDisabled ? 'not-allowed' : 'pointer',
            opacity: nextStepDisabled ? 0.7 : 1,
          }}
        >
          {t('courses.create.next_step')}
          <img
            src={iconArrowRightHeaderCourses}
            alt=""
            width={18}
            height={18}
            aria-hidden
            style={{ display: 'block' }}
          />
        </button>
      </div>
    </header>
  );
}
